import { DataSource } from "@angular/cdk/collections";
import { Branch } from "@core/models/Branch";
import { BehaviorSubject, merge, Observable } from "rxjs";
import { BranchService } from "@core/http/table-data-services/Branch.service";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { map } from "rxjs/operators";

export class BranchDataSource extends DataSource<Branch> {
  filterChange = new BehaviorSubject<string>('');
  filteredData: Branch[] = [];
  renderedData: Branch[] = [];

  constructor(
    public businessGroupId: string | null,
    public exampleDatabase: BranchService,
    public paginator: MatPaginator,
    public _sort: MatSort,
    public soloActivas?: boolean
  ) {
    super();
    this.filterChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  get filter(): string {
    return this.filterChange.value;
  }

  set filter(filter: string) {
    this.filterChange.next(filter);
  }

  connect(): Observable<Branch[]> {
    const displayDataChanges = [
      this.exampleDatabase.dataChange,
      this._sort.sortChange,
      this.filterChange,
      this.paginator.page,
    ];

    return merge(...displayDataChanges).pipe(
      map(() => {
        this.filteredData = this.exampleDatabase.data
          .slice()
          .filter((branch: Branch) => {
            const searchStr = (
              (branch.idSucursal ?? '') +
              (branch.nombre ?? '') +
              (branch.registroSanitario ?? '') +
              (branch.responsable ?? '') +
              (branch.cedulaResponsable ?? '') +
              (branch.telefonoResponsable ?? '') +
              (branch.emailResponsable ?? '') +
              (branch.domicilio ?? '') +
              (branch.idAsentamiento ?? '') +
              (branch.nombreAsentamiento ?? '') +
              (branch.tipoAsentamiento ?? '') +
              (branch.ciudad ?? '') +
              (branch.codigoPostal ?? '') +
              (branch.municipio ?? '') +
              (branch.estado ?? '') +
              (branch.abreviatura ?? '') +
              (branch.status ?? '')
            ).toLowerCase();

            return searchStr.indexOf(this.filter.toLowerCase()) !== -1;
          });

        const sortedData = this.sortData(this.filteredData.slice());

        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this.paginator.pageSize);

        return this.renderedData;
      })
    );
  }

  disconnect(): void {}

  sortData(data: Branch[]): Branch[] {
    if (!this._sort.active || this._sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      let propertyA: number | string = '';
      let propertyB: number | string = '';

      switch (this._sort.active) {
        case 'nombre':
          [propertyA, propertyB] = [a.nombre ?? '', b.nombre ?? ''];
          break;

        case 'registroSanitario':
          [propertyA, propertyB] = [a.registroSanitario ?? '', b.registroSanitario ?? ''];
          break;

        case 'responsable':
          [propertyA, propertyB] = [a.responsable ?? '', b.responsable ?? ''];
          break;

        case 'cedulaResponsable':
          [propertyA, propertyB] = [a.cedulaResponsable ?? '', b.cedulaResponsable ?? ''];
          break;

        case 'telefonoResponsable':
          [propertyA, propertyB] = [a.telefonoResponsable ?? '', b.telefonoResponsable ?? ''];
          break;

        case 'emailResponsable':
          [propertyA, propertyB] = [a.emailResponsable ?? '', b.emailResponsable ?? ''];
          break;

        case 'domicilio':
        case 'direccion':
          [propertyA, propertyB] = [a.domicilio ?? '', b.domicilio ?? ''];
          break;

        case 'status':
          [propertyA, propertyB] = [a.status ?? '', b.status ?? ''];
          break;

        default:
          [propertyA, propertyB] = ['', ''];
          break;
      }

      const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
      const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

      if (valueA === valueB) {
        return 0;
      }

      return (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1);
    });
  }
}
